import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
} from "react-native";
import React, { useEffect, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";
import CustomAlert from "../Components/CustomAlert";
import { observer, Observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useScreenDimensions, useDevice } from "../_layout";
import { ScrollView } from "react-native-web";
import { Ionicons } from "@expo/vector-icons";
import { Feather } from "@expo/vector-icons";
import { ProtocolTitles } from "../../Resources/Constants";
import ProtocolRow from "./ProtocolRow";
import IconEmptyView from "../Components/IconEmptyView";
import ScrollButtons from "../Components/ScrollButtons";
import { useToast } from "../contexts/ToastContext";
import Tooltip from "../Components/Tooltip";

const FullBlockView = React.memo(
  ({
    block,
    scrollHelpers = false,
    calendar = false,
    useScrollContainer = true, // when false, disable internal scrolling
    weekVisibleRange = null, // external visible range (when useScrollContainer is false)
  }) => {
    const { t, i18n } = useTranslation();
    const { screenSize } = useScreenDimensions();
    const { isMobile } = useDevice();
    const { showToast } = useToast();

    // console.log("FullBlockView rendered");
    const currentWeek = block.client && block.isActive && block.currentWeek();
    const showSetPriority =
      block?.client?.coach.showSetPriority ??
      block?.coach?.showSetPriority ??
      true;

    const WeekHeader = observer(({ week }) => {
      const [isHovered, setIsHovered] = useState(false);
      const [isHoveredEdit, setIsHoveredEdit] = useState(false);
      const [isHoveredEye, setIsHoveredEye] = useState(false);

      const [isEditing, setIsEditing] = useState(false);

      const [weekName, setWeekName] = useState(
        block.getWeekName(week) ? block.getWeekName(week) : ""
      );

      const [
        showingExerciseWithDataWarning,
        setShowingExerciseWithDataWarning,
      ] = useState(false);

      const finishEditing = () => {
        if (weekName !== "") {
          block.setWeekName(week, weekName);
        } else {
          block.deleteWeekName(week);
        }
        setIsEditing(false);
      };

      const checkCopyProtocolWarning = () => {
        const useWarning = block.checkIfSeriesWithData(week + 1);
        if (useWarning) {
          setShowingExerciseWithDataWarning(true);
        } else {
          block.copyWeekOfProtocols(week);
        }
      };

      const ProtocolGuideButton = ({}) => {
        const showingProtocolsGuide =
          block.client?.coach?.showingProtocolsGuide ||
          block.coach?.showingProtocolsGuide;
        const noSlotsInBlock =
          block.protocols.length === 0 &&
          !block.workouts.some((w) => w.exercises.some((e) => e.protocolSlot));

        const [isHoveredProtocol, setIsHoveredProtocol] = useState(false);
        const onPress = () => {
          if (noSlotsInBlock) return;

          const coach = block.coach ?? block.client?.coach;
          coach.setProperty(
            "showingProtocolsGuide",
            !coach.showingProtocolsGuide
          );
        };
        return (
          <TouchableOpacity
            onPress={onPress}
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              outlineStyle: "none",
            }}
            onMouseEnter={() => !isMobile && setIsHoveredProtocol(true)}
            onMouseLeave={() => setIsHoveredProtocol(false)}
          >
            <Text
              style={{
                textAlign: "center",
                fontSize: 13,
                fontWeight: "500",
                color: isHoveredProtocol
                  ? Colors.darkgrayColor
                  : Colors.grayColor,
              }}
              numberOfLines={1}
            >
              {t("protocol-string")}
            </Text>
            <Ionicons
              name={
                showingProtocolsGuide && !noSlotsInBlock
                  ? "information-circle"
                  : "information-circle-outline"
              }
              size={14}
              color={
                isHoveredProtocol ? Colors.darkgrayColor : Colors.grayColor
              }
              style={{ marginLeft: 4, marginTop: 1 }}
            />
          </TouchableOpacity>
        );
      };

      return (
        <View
          style={{
            width: block.showingDashboardActual ? 480 : 264,
            alignItems: "flex-start",
            flexDirection: "row",
            marginRight: week !== block.totalWeeks ? 40 : 16,
          }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <View style={{ alignItems: "center", width: "100%" }}>
            {isEditing ? (
              <View
                style={{
                  // maxWidth: 264,
                  width: "100%",
                  justifyContent: "center",
                  flexDirection: "row",
                  paddingLeft: 4,
                }}
              >
                <TextInput
                  style={{
                    flex: 1,
                    color: Colors.grayColor,
                    outlineStyle: "none",
                    fontSize: 13,
                  }}
                  placeholder={t("newWeekName-string")}
                  placeholderTextColor={"rgba(0, 0, 0, 0.2)"}
                  value={weekName}
                  onChangeText={setWeekName}
                  onSubmitEditing={finishEditing}
                  onKeyPress={({ nativeEvent }) => {
                    if (nativeEvent.key === "Escape") {
                      setIsEditing(false);
                    }
                  }}
                  maxLength={30}
                />
                <Text
                  style={{
                    textAlign: "center",
                    fontSize: 13,
                    color:
                      weekName === "" ? Colors.grayColor : Colors.bubbleColor,
                    paddingHorizontal: 4,
                  }}
                  numberOfLines={1}
                  onPress={finishEditing}
                >
                  {weekName === "" ? t("cancel-string") : t("save-string")}
                </Text>
              </View>
            ) : (
              <View
                style={{
                  width: block.showingDashboardActual ? 480 : 264,
                  justifyContent: "center",
                  flexDirection: "row",
                }}
              >
                <Observer>
                  {() => (
                    <Tooltip
                      content={
                        block.isWeekHidden(week)
                          ? "showWeek-string"
                          : "hideWeek-string"
                      }
                      placement="top"
                      delayedOpen={true}
                    >
                      <TouchableOpacity
                        style={{
                          paddingRight: 8,
                          alignItems: "center",
                          outlineStyle: "none",
                          outlineWidth: 0,
                          outlineColor: "transparent",
                        }}
                        onPress={() => block.toggleHiddenWeek(week)}
                        disabled={week === 1}
                        onMouseEnter={() => !isMobile && setIsHoveredEye(true)}
                        onMouseLeave={() => setIsHoveredEye(false)}
                      >
                        <Feather
                          name={block.isWeekHidden(week) ? "eye-off" : "eye"}
                          size={14}
                          color={
                            currentWeek === week
                              ? Colors.bubbleColor
                              : Colors.grayColor
                          }
                          style={{
                            opacity: week === 1 ? 0 : isHoveredEye ? 0.6 : 1,
                          }}
                        />
                      </TouchableOpacity>
                    </Tooltip>
                  )}
                </Observer>

                <Observer>
                  {() => (
                    <Text
                      style={{
                        textAlign: "center",
                        fontSize: 13,
                        fontWeight: "500",
                        color:
                          currentWeek === week
                            ? Colors.bubbleColor
                            : Colors.grayColor,
                        maxWidth: 220,
                      }}
                      numberOfLines={1}
                    >
                      {block.getWeekName(week)
                        ? block.getWeekName(week)
                        : t("weekWithNumber-string", {
                            week: week,
                          })}
                    </Text>
                  )}
                </Observer>
                <TouchableOpacity
                  style={{
                    opacity:
                      isHovered || isMobile ? (isHoveredEdit ? 0.6 : 1) : 0,
                    alignItems: "center",
                    paddingLeft: 8,
                    outlineStyle: "none",
                  }}
                  onPress={() => setIsEditing(true)}
                  onMouseEnter={() => !isMobile && setIsHoveredEdit(true)}
                  onMouseLeave={() => setIsHoveredEdit(false)}
                >
                  <Feather
                    name="edit-2"
                    size={13}
                    color={
                      currentWeek === week
                        ? Colors.bubbleColor
                        : Colors.grayColor
                    }
                  />
                </TouchableOpacity>
              </View>
            )}

            <View
              style={{
                height: 1,
                backgroundColor: Colors.borderColor,
                width: "100%",
                marginVertical: 4,
              }}
            />

            <View
              style={[
                GlobalStyles.HStack,
                {
                  width: "100%",
                  // justifyContent: "space-between",
                  gap: 16,
                },
              ]}
            >
              <View
                style={{
                  flex: 1,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{
                    textAlign: "center",
                    fontSize: 13,
                    fontWeight: "500",
                    color: Colors.grayColor,
                  }}
                  numberOfLines={1}
                >
                  Slot
                </Text>
                {week === 1 && (
                  <Tooltip content="slotExplanation-string" placement="top">
                    <View>
                      <Ionicons
                        name="information-circle-outline"
                        size={14}
                        color={Colors.grayColor}
                        style={{ marginLeft: 4, marginTop: 1 }}
                      />
                    </View>
                  </Tooltip>
                )}
              </View>
              <View
                style={{
                  flex: 1,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  // textAlign: "center",
                  // fontSize: 13,
                  // fontWeight: "500",
                  // color: Colors.grayColor,
                }}
              >
                {week === 1 ? (
                  <ProtocolGuideButton />
                ) : (
                  <Text
                    style={{
                      textAlign: "center",
                      fontSize: 13,
                      fontWeight: "500",
                      color: Colors.grayColor,
                    }}
                    numberOfLines={1}
                  >
                    {t("protocol-string")}
                  </Text>
                )}
              </View>
            </View>
          </View>

          <Tooltip
            content="copyWeekSlotsAndProtocols-string"
            placement="top"
            delayedOpen={true}
          >
            <TouchableOpacity
              onPress={checkCopyProtocolWarning}
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 8,
                alignSelf: "center",
                opacity:
                  (isHovered || isMobile) & (week !== block.totalWeeks) ? 1 : 0,
                marginLeft: 8,
                width: 24,
                outlineStyle: "none",
              }}
            >
              <Ionicons
                name="arrow-forward-circle-outline"
                size={20}
                color={Colors.bubbleColor}
                style={{
                  paddingVertical: 12,
                  // paddingHorizontal: 2,
                }}
              />
            </TouchableOpacity>
          </Tooltip>
          {showingExerciseWithDataWarning && (
            <CustomAlert
              isVisible={showingExerciseWithDataWarning}
              onClose={() => setShowingExerciseWithDataWarning(false)}
              title={"actualWithDataCopyWeekProtocolsWarningTitle-string"}
              text={t("actualWithDataCopyWeekProtocolsWarningText-string", {
                week: week + 1 ?? 1,
              })}
              action={() => {
                block.copyWeekOfProtocols(week);
              }}
              specialCTA={"replace-string"}
            />
          )}
        </View>
      );
    });

    const ShowActualButton = observer(({}) => {
      const noSlotsInBlock =
        block.protocols.length === 0 &&
        !block.workouts.some((w) => w.exercises.some((e) => e.protocolSlot));
      const [isHovered, setIsHovered] = useState(false);
      const onPress = () => {
        if (noSlotsInBlock) return;

        if (!block.showingDashboardActual) {
          const uniqueProtocols = block.toggleAllProtocols();

          if (calendar && block.client && uniqueProtocols) {
            const macroBlocks = block.client.blocks.filter(
              (b) => b.label === block.label && b.id != block.id
            );
            macroBlocks.forEach((b) => {
              uniqueProtocols.forEach((protocol) => {
                if (!b.protocolSeriesShown.includes(protocol)) {
                  b.protocolSeriesShown.push(protocol);
                }
              });
            });
          }
        }

        block.setProperty(
          "showingDashboardActual",
          !block.showingDashboardActual
        );
      };
      return (
        <Tooltip
          content={
            noSlotsInBlock
              ? "startAddingSlotsBeforeShowingActual-string"
              : !block.showingDashboardActual
              ? "showActualOfSets-string"
              : "hideActualOfSets-string"
          }
          placement="top"
          delayedOpen={!noSlotsInBlock}
        >
          <TouchableOpacity
            onPress={onPress}
            disabled={noSlotsInBlock}
            style={{
              padding: 8,
              backgroundColor: noSlotsInBlock
                ? "rgba(156, 156, 156, 0.1)"
                : isHovered
                ? "rgba(63, 123, 141, 0.1)"
                : block.showingDashboardActual
                ? "rgba(63, 123, 141, 0.05)"
                : "rgba(63, 123, 141, 0.08)",
              borderRadius: 10,
              outlineStyle: "none",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Text
              style={{
                color: noSlotsInBlock
                  ? Colors.grayColor
                  : block.showingDashboardActual
                  ? Colors.grayColor
                  : Colors.bubbleColor,
              }}
            >
              {block.showingDashboardActual
                ? t("hideActual-string")
                : t("showActual-string")}
            </Text>
          </TouchableOpacity>
        </Tooltip>
      );
    });

    /* HANDLE DRAG&DROP */
    // Check if this block already has slots in workouts
    const [
      showingNotPossibleToCopyWarning,
      setShowingNotPossibleToCopyWarning,
    ] = useState(false);
    const [showingCopyBlockWarning, setShowingCopyBlockWarning] =
      useState(false);
    const [sourceBlock, setSourceBlock] = useState(null);

    const hasSeriesWithData = block.checkIfSeriesWithData();

    const [{ isOver, canDrop }, dropRef] = useDrop({
      accept: "BLOCK_SEGMENT",
      canDrop: (item) => item?.block?.id !== block?.id,
      drop: (seg, monitor) => {
        console.log("Dropped item:", seg);
        const draggedBlock = seg?.block;
        setSourceBlock(draggedBlock || null);
        if (!draggedBlock) return;

        if (!hasSeriesWithData) {
          // Check if block has no protocols and no workouts with exercises
          const hasNoProtocols =
            !block.protocols || block.protocols.length === 0;
          const workoutsHaveNoExercises =
            !block.workouts ||
            block.workouts.every((w) => w.exercises.length === 0);

          if (hasNoProtocols && workoutsHaveNoExercises) {
            // Copy directly
            const client = block.client;
            if (!client) return;
            client.copyBlock(t, draggedBlock, true, block, showToast);
            client.updateClientTransactionData(block.id);
            client.startOrResetBatchTimer();

            setSourceBlock(null);
          } else {
            // Show warning: block has protocols and/or workouts
            setShowingCopyBlockWarning(true);
          }
        } else {
          setShowingNotPossibleToCopyWarning(true);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    });

    const copySourceBlock = () => {
      const client = block.client;

      if (!sourceBlock || !client) return;

      client.copyBlock(t, sourceBlock, true, block, showToast);
      client.updateClientTransactionData(block.id);
      client.startOrResetBatchTimer();

      setShowingCopyBlockWarning(false);
      setSourceBlock(null);
    };

    /* SCROLL HELPERS */
    const scrollRef = useRef(null);
    const lastOffsetX = useRef(0);

    // --- Arrow key fixed-step scroll helpers ---
    const scrollRightFixed = () => {
      const scrollStep = (block.showingDashboardActual ? 264 + 216 : 264) + 40;
      const node = scrollRef.current;
      if (!node) return;
      const maxOffset = node.scrollWidth - node.clientWidth;
      const newX = Math.min(lastOffsetX.current + scrollStep, maxOffset);
      lastOffsetX.current = newX;
      node.scrollTo({ x: newX, animated: false });
    };

    const scrollLeftFixed = () => {
      const scrollStep = (block.showingDashboardActual ? 264 + 216 : 264) + 40;
      const node = scrollRef.current;
      if (!node) return;
      const newX = Math.max(lastOffsetX.current - scrollStep, 0);
      lastOffsetX.current = newX;
      node.scrollTo({ x: newX, animated: false });
    };

    useEffect(() => {
      if (!scrollHelpers) return;
      const handleKeyDown = (e) => {
        if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
          e.preventDefault();
          if (e.key === "ArrowRight") {
            scrollRightFixed();
          } else {
            scrollLeftFixed();
          }
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }, []);

    const BlockContent = observer(() => {
      const totalWeeksLocal = block.totalWeeks || 0;
      const colWidthLocal = block.showingDashboardActual ? 480 : 264;

      // Use external weekVisibleRange if provided and weekVirtualization is enabled,
      // otherwise show all weeks
      const rowsVisibleRange =
        weekVisibleRange !== null
          ? weekVisibleRange
          : { start: 0, end: Math.max(0, totalWeeksLocal - 1) };

      return (
        <View
          style={{
            backgroundColor:
              isOver && canDrop
                ? hasSeriesWithData
                  ? "rgba(141, 63, 63, 0.22)"
                  : "rgba(63, 123, 141, 0.16)"
                : "white",
            borderRadius: 14,
            paddingVertical: 16,
            paddingHorizontal: 16,
            borderRadius: 14,
            alignItems: "center",
            borderWidth: 0.1,
            borderColor: Colors.borderColor,
            alignSelf: "flex-start",
          }}
        >
          {canDrop && (
            <View
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                right: 0,
                left: 0,
                zIndex: 2,
                backgroundColor: "rgba(168, 172, 173, 0.20)",
                borderRadius: 14,
              }}
            >
              <View
                style={{
                  maxWidth: "100%",
                  width: 340,
                  alignSelf: "center",
                  marginTop: 48,
                  paddingTop: 36,
                  paddingBottom: 8,
                  borderRadius: 12,
                }}
              >
                <IconEmptyView
                  iconLibrary={"Ionicons"}
                  mainText={"dropCalendarBlockToCopyTitle-string"}
                  subText={"dropCalendarBlockToCopyText-string"}
                  iconName={"copy-outline"}
                />
              </View>
            </View>
          )}
          <View style={{ opacity: canDrop ? 0.15 : 1, borderRadius: 14 }}>
            <View
              style={{
                width: "100%",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <View
                style={{
                  width: 180,
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}
              >
                <ShowActualButton />
              </View>
              <Observer>
                {() =>
                  Array.from({ length: block.totalWeeks }, (_, i) => i + 1).map(
                    (week) => <WeekHeader week={week} key={week} />
                  )
                }
              </Observer>
            </View>

            <>
              <View
                style={{
                  alignItems: "center",
                  borderRadius: 14,
                  borderWidth: 0.1,
                  borderColor: Colors.borderColor,
                  paddingVertical: 8,
                  marginTop: 16,
                  gap: 8,
                }}
              >
                <ProtocolRow
                  block={block}
                  protocolTitle={ProtocolTitles.PRIMARY_SQUAT}
                  calendar={calendar}
                  visibleRange={rowsVisibleRange}
                  columnWidth={colWidthLocal}
                  showSetPriority={showSetPriority}
                />
                {block.canShowProtocolRow("squat", "primary") && (
                  <ProtocolRow
                    block={block}
                    protocolTitle={ProtocolTitles.SECONDARY_SQUAT}
                    calendar={calendar}
                    visibleRange={rowsVisibleRange}
                    columnWidth={colWidthLocal}
                    showSetPriority={showSetPriority}
                  />
                )}
                {block.canShowProtocolRow("squat", "secondary") && (
                  <ProtocolRow
                    block={block}
                    calendar={calendar}
                    protocolTitle={ProtocolTitles.TERTIARY_SQUAT}
                    visibleRange={rowsVisibleRange}
                    columnWidth={colWidthLocal}
                    showSetPriority={showSetPriority}
                  />
                )}
                {block.canShowProtocolRow("squat", "tertiary") && (
                  <ProtocolRow
                    block={block}
                    calendar={calendar}
                    protocolTitle={ProtocolTitles.QUATERNARY_SQUAT}
                    visibleRange={rowsVisibleRange}
                    columnWidth={colWidthLocal}
                    showSetPriority={showSetPriority}
                  />
                )}
                {block.canShowProtocolRow("squat", "quaternary") && (
                  <ProtocolRow
                    block={block}
                    protocolTitle={ProtocolTitles.QUINARY_SQUAT}
                    calendar={calendar}
                    visibleRange={rowsVisibleRange}
                    columnWidth={colWidthLocal}
                    showSetPriority={showSetPriority}
                  />
                )}
              </View>

              <View
                style={{
                  alignItems: "center",
                  borderRadius: 14,
                  borderWidth: 0.1,
                  borderColor: Colors.borderColor,
                  paddingVertical: 8,
                  marginTop: 16,
                  gap: 8,
                }}
              >
                <ProtocolRow
                  block={block}
                  protocolTitle={ProtocolTitles.PRIMARY_BENCH_PRESS}
                  calendar={calendar}
                  visibleRange={rowsVisibleRange}
                  columnWidth={colWidthLocal}
                  showSetPriority={showSetPriority}
                />
                {block.canShowProtocolRow("benchPress", "primary") && (
                  <ProtocolRow
                    block={block}
                    protocolTitle={ProtocolTitles.SECONDARY_BENCH_PRESS}
                    calendar={calendar}
                    visibleRange={rowsVisibleRange}
                    columnWidth={colWidthLocal}
                    showSetPriority={showSetPriority}
                  />
                )}
                {block.canShowProtocolRow("benchPress", "secondary") && (
                  <ProtocolRow
                    block={block}
                    protocolTitle={ProtocolTitles.TERTIARY_BENCH_PRESS}
                    calendar={calendar}
                    visibleRange={rowsVisibleRange}
                    columnWidth={colWidthLocal}
                    showSetPriority={showSetPriority}
                  />
                )}
                {block.canShowProtocolRow("benchPress", "tertiary") && (
                  <ProtocolRow
                    block={block}
                    protocolTitle={ProtocolTitles.QUATERNARY_BENCH_PRESS}
                    calendar={calendar}
                    visibleRange={rowsVisibleRange}
                    columnWidth={colWidthLocal}
                    showSetPriority={showSetPriority}
                  />
                )}
                {block.canShowProtocolRow("benchPress", "quaternary") && (
                  <ProtocolRow
                    block={block}
                    protocolTitle={ProtocolTitles.QUINARY_BENCH_PRESS}
                    calendar={calendar}
                    visibleRange={rowsVisibleRange}
                    columnWidth={colWidthLocal}
                    showSetPriority={showSetPriority}
                  />
                )}
              </View>

              <View
                style={{
                  alignItems: "center",
                  borderRadius: 14,
                  borderWidth: 0.1,
                  borderColor: Colors.borderColor,
                  paddingVertical: 8,
                  marginTop: 16,
                  gap: 8,
                }}
              >
                <ProtocolRow
                  block={block}
                  protocolTitle={ProtocolTitles.PRIMARY_DEADLIFT}
                  calendar={calendar}
                  visibleRange={rowsVisibleRange}
                  columnWidth={colWidthLocal}
                  showSetPriority={showSetPriority}
                />
                {block.canShowProtocolRow("deadlift", "primary") && (
                  <ProtocolRow
                    block={block}
                    protocolTitle={ProtocolTitles.SECONDARY_DEADLIFT}
                    calendar={calendar}
                    visibleRange={rowsVisibleRange}
                    columnWidth={colWidthLocal}
                    showSetPriority={showSetPriority}
                  />
                )}
                {block.canShowProtocolRow("deadlift", "secondary") && (
                  <ProtocolRow
                    block={block}
                    protocolTitle={ProtocolTitles.TERTIARY_DEADLIFT}
                    calendar={calendar}
                    visibleRange={rowsVisibleRange}
                    columnWidth={colWidthLocal}
                    showSetPriority={showSetPriority}
                  />
                )}
                {block.canShowProtocolRow("deadlift", "tertiary") && (
                  <ProtocolRow
                    block={block}
                    protocolTitle={ProtocolTitles.QUATERNARY_DEADLIFT}
                    calendar={calendar}
                    visibleRange={rowsVisibleRange}
                    columnWidth={colWidthLocal}
                    showSetPriority={showSetPriority}
                  />
                )}
                {block.canShowProtocolRow("deadlift", "quaternary") && (
                  <ProtocolRow
                    block={block}
                    protocolTitle={ProtocolTitles.QUINARY_DEADLIFT}
                    calendar={calendar}
                    visibleRange={rowsVisibleRange}
                    columnWidth={colWidthLocal}
                    showSetPriority={showSetPriority}
                  />
                )}
              </View>
            </>
          </View>
        </View>
      );
    });

    return (
      <View
        ref={dropRef}
        style={{
          flex: 1,
          alignItems: "center",
        }}
      >
        <View style={{ maxWidth: "100%" }}>
          {!isMobile && scrollHelpers && (
            <>
              <ScrollButtons
                scrollRef={scrollRef}
                lastOffsetX={lastOffsetX}
                scrollDirection="right"
              />
              <ScrollButtons
                scrollRef={scrollRef}
                lastOffsetX={lastOffsetX}
                scrollDirection="left"
              />
            </>
          )}

          {useScrollContainer ? (
            <ScrollView
              ref={scrollRef}
              showsHorizontalScrollIndicator={false}
              horizontal={true}
              scrollEnabled={true}
              onScroll={(e) =>
                (lastOffsetX.current = e.nativeEvent.contentOffset.x)
              }
              scrollEventThrottle={48}
              contentContainerStyle={{ flexGrow: 1 }}
              style={{ width: "100%" }}
            >
              <BlockContent />
            </ScrollView>
          ) : (
            <View ref={scrollRef} style={{ width: "100%" }}>
              <BlockContent />
            </View>
          )}
        </View>
        {showingNotPossibleToCopyWarning && (
          <CustomAlert
            isVisible={showingNotPossibleToCopyWarning}
            onClose={() => {
              setShowingNotPossibleToCopyWarning(false);
              setSourceBlock(null);
            }}
            title={"actualWithDataCopyCalendarBlockTitle-string"}
            text={t("actualWithDataCopyCalendarBlockText-string", {
              blockName: block.name,
            })}
          />
        )}
        {showingCopyBlockWarning && (
          <CustomAlert
            isVisible={showingCopyBlockWarning}
            onClose={() => {
              setShowingCopyBlockWarning(false);
              setSourceBlock(null);
            }}
            title={"copyCalendarBlockWarningTitle-string"}
            text={t("copyCalendarBlockWarningText-string", {
              sourceBlockName: sourceBlock?.name || t("block-string"),
              targetBlockName: block.name,
            })}
            action={copySourceBlock}
            specialCTA={"copy-string"}
            {...(block && block.workouts.some((w) => w.exercises.length > 0)
              ? {
                  doubleConfirmationText: t(
                    "copyCalendarBlockDoubleConfirmationText-string"
                  ),
                }
              : {})}
          />
        )}
      </View>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function for React.memo
    // Return true if props are equal (should NOT re-render)
    // Return false if props are different (should re-render)

    // Compare basic props
    if (
      prevProps.scrollHelpers !== nextProps.scrollHelpers ||
      prevProps.calendar !== nextProps.calendar ||
      prevProps.useScrollContainer !== nextProps.useScrollContainer ||
      prevProps.weekVirtualization !== nextProps.weekVirtualization
    ) {
      return false;
    }

    // Compare block reference and key properties
    if (prevProps.block !== nextProps.block) {
      return false;
    }

    // Deep compare weekVisibleRange
    const prevRange = prevProps.weekVisibleRange;
    const nextRange = nextProps.weekVisibleRange;

    if (prevRange === nextRange) {
      return true; // Same reference, no change
    }

    if (!prevRange || !nextRange) {
      return false; // One is null/undefined, other isn't
    }

    if (
      prevRange.start !== nextRange.start ||
      prevRange.end !== nextRange.end
    ) {
      return false; // Range values changed
    }

    return true; // All props are equal
  }
);

const styles = StyleSheet.create({});

export default FullBlockView;
